import React, { Component } from 'react';
import { calculateAnimationState } from './utils/frameCalculator';
import { renderHighlightIcon } from './utils/highlightIcons';

class VehicleHighlightsSVG extends Component {
 
  static BASE_WIDTH = 360;
  static BASE_HEIGHT = 780;
  
  // Grey Pill dimensions (from highlights.svg)
  // Exact dimensions from SVG: height 88, radius 44
  static PILL_WIDTH = 300;  // Base width for pills
  static PILL_HEIGHT = 88;  // Matches highlights.svg exactly
  static PILL_RADIUS = 44;  // Fully rounded ends (half of height)
  
  // Blue Circle dimensions (from highlights.svg)
  // Exact dimensions from SVG: outer radius 49, inner radius 40
  static CIRCLE_SIZE = 98;       // Outer circle diameter (49 * 2)
  static CIRCLE_RADIUS = 49;     // Outer circle radius
  static INNER_CIRCLE_RADIUS = 40;  // Inner circle radius
  static CIRCLE_OVERLAP = 40;    // Circle overlaps into pill
  static LEFT_PILL_EXTENSION = 30;  // Extra width for pills 3,5 to align with snake curve
  static RIGHT_PILL_EXTENSION = 30; // Extra width for pills 2,4,6 to align with snake curve

  constructor(props) {
    super(props);
    this.svgRef = React.createRef();
    this.snakePathRef = React.createRef();
    this.snakePathLength = null;
  }
  
  componentDidMount() {
    // Measure actual snake path length for accurate animation
    if (this.snakePathRef.current) {
      this.snakePathLength = this.snakePathRef.current.getTotalLength();
    }
  }
  
  componentDidUpdate() {
    // Update path length if snake path changes
    if (this.snakePathRef.current && !this.snakePathLength) {
      this.snakePathLength = this.snakePathRef.current.getTotalLength();
    }
  }

  render() {
    const {
      currentFrame = 0,
      totalFrames = 1,
      enabled = false,
      vehicleData = {},
      specItemsConfig = [],
      debug = false,
    } = this.props;

    if (!enabled || !specItemsConfig || specItemsConfig.length === 0) {
      return null;
    }

    // Get animation state
    const animationState = calculateAnimationState(currentFrame, totalFrames);
    const { phase, isVisible, itemStates, snakeProgress } = animationState;

    // Base dimensions (for 1470px viewport) - use class constants
    const BASE_WIDTH = VehicleHighlightsSVG.BASE_WIDTH;
    const BASE_HEIGHT = VehicleHighlightsSVG.BASE_HEIGHT;
    const PILL_WIDTH = VehicleHighlightsSVG.PILL_WIDTH;
    const PILL_HEIGHT = VehicleHighlightsSVG.PILL_HEIGHT;
    const PILL_RADIUS = VehicleHighlightsSVG.PILL_RADIUS;
    const CIRCLE_SIZE = VehicleHighlightsSVG.CIRCLE_SIZE;
    const CIRCLE_RADIUS = VehicleHighlightsSVG.CIRCLE_RADIUS;

    const PILL_POSITIONS = [
      { y: 67, iconSide: 'left' },    
      { y: 193, iconSide: 'right' }, 
      { y: 328, iconSide: 'left' },   
      { y: 455, iconSide: 'right' },  
      { y: 581, iconSide: 'left' },   
      { y: 713, iconSide: 'right' },  
    ];

    const getPillWidth = (iconSide, index) => {
      const LEFT_EXTENSION = VehicleHighlightsSVG.LEFT_PILL_EXTENSION;
      const RIGHT_EXTENSION = VehicleHighlightsSVG.RIGHT_PILL_EXTENSION;
      
      const exactWidths = [
        297 + RIGHT_EXTENSION,  
        297 + LEFT_EXTENSION,   
        297 + RIGHT_EXTENSION, 
        297 + LEFT_EXTENSION,   
        297 + RIGHT_EXTENSION, 
        297 + LEFT_EXTENSION   
      ];
      return exactWidths[index] || VehicleHighlightsSVG.PILL_WIDTH;
    };

    const getPillX = (iconSide, index) => {
      const LEFT_EXTENSION = VehicleHighlightsSVG.LEFT_PILL_EXTENSION;
      
      if (iconSide === 'right') {
       
        return 30 - LEFT_EXTENSION;
      } else {
        // Circle on LEFT: base position
        return 30;
      }
    };

    // Calculate circle X positions (center of circle)
    const getCircleX = (iconSide, index, pillX) => {
      const CIRCLE_OVERLAP = VehicleHighlightsSVG.CIRCLE_OVERLAP;
      if (iconSide === 'right') {
        const actualPillWidth = getPillWidth(iconSide, index);
        return pillX + actualPillWidth - CIRCLE_OVERLAP;
      } else {
        // Circle on LEFT side of pill (overlapping)
        return pillX + CIRCLE_RADIUS;
      }
    };

    const wrapText = (text, maxCharsPerLine = 20, maxLines = 3) => {
      if (!text) return [];
      const words = String(text).split(' ');
      const lines = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        
        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            // Check if the word itself is too long and break it
            if (word.length > maxCharsPerLine) {
              lines.push(word.slice(0, maxCharsPerLine));
              currentLine = word.slice(maxCharsPerLine);
            } else {
              currentLine = word;
            }
          } else {
            // Word itself is too long, break it
            lines.push(word.slice(0, maxCharsPerLine));
            currentLine = word.slice(maxCharsPerLine);
          }
        }
        
        // Stop if we've reached max lines
        if (lines.length >= maxLines && currentLine) {
          break;
        }
      }
      
      if (currentLine && lines.length < maxLines) {
        lines.push(currentLine);
      }
      
      // Add ellipsis to last line if there's more text
      if (lines.length === maxLines && currentLine && words.join(' ').length > lines.join(' ').length) {
        lines[maxLines - 1] = lines[maxLines - 1].slice(0, -3) + '...';
      }
      
      return lines.slice(0, maxLines);
    };


    return (
      <div
        style={{
          width: `${BASE_WIDTH}px`,
          height: `${BASE_HEIGHT}px`,
          // Ensure overflow is visible to prevent cropping on small screens
          overflow: 'visible',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
      >
        <svg
          ref={this.svgRef}
          viewBox={`0 0 ${BASE_WIDTH} ${BASE_HEIGHT}`}
          width="100%"
          height="100%"
          style={{
            overflow: 'visible',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {/* Gradient Definitions - From actual SVG assets */}
          <defs>
            {/* Grey Pill Gradient (from grey_pill.svg) */}
            <linearGradient id="pillGradient" x1="111.111" y1="46.531" x2="651.592" y2="150.594" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#ffffff"/>
              <stop offset="1" stopColor="#EDEDED"/>
            </linearGradient>

            <linearGradient id="circleGradient" x1="50%" y1="0%" x2="50%" y2="100%" gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="white"/>
              <stop offset="1" stopColor="#999999"/>
            </linearGradient>

            <linearGradient id="circleBorderGradient" x1="0%" y1="50%" x2="100%" y2="50%" gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#2D70D4" stopOpacity="0"/>
              <stop offset="1" stopColor="white"/>
            </linearGradient>

            <linearGradient id="innerCircleGradient" x1="50%" y1="0%" x2="50%" y2="100%" gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="white"/>
              <stop offset="1" stopColor="#999999"/>
            </linearGradient>

          
            <linearGradient id="innerCircleBorderGradient" x1="0%" y1="50%" x2="100%" y2="50%" gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#2D70D4" stopOpacity="0"/>
              <stop offset="1" stopColor="white"/>
            </linearGradient>

          
            <linearGradient id="snakeLineGradient" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#000000"/>
              <stop offset="1" stopColor="#443A5F"/>
            </linearGradient>

            {/* Drop Shadow Filter */}
            <filter id="pillShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="2" dy="3" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.2"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Circle Shadow Filter - EXACT from outer.svg (filter0_d_57_464) */}
            <filter id="circleShadow" x="-50%" y="-50%" width="200%" height="200%" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset dx="-5.18682" dy="9.33407"/>
              <feGaussianBlur stdDeviation="4.14725"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0.113725 0 0 0 0 0.0941176 0 0 0 0 0 0 0 0 0.3 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_57_464"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_57_464" result="shape"/>
            </filter>

            {/* Text Shadow Filter */}
            <filter id="textShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="2" dy="2" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.4"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Snake Line Shadow Filter - From HighlightFinal */}

            <filter id="snakeLineShadow" x="-50%" y="-50%" width="200%" height="200%" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset dy="4"/>
              <feGaussianBlur stdDeviation="2"/>
              <feComposite in2="hardAlpha" operator="out"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_78"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_78" result="shape"/>
            </filter>
          </defs>

          {/* Render Pills */}
          {specItemsConfig.map((item, index) => {
            if (index >= 6) return null; // Max 6 pills

            const position = PILL_POSITIONS[index];
            const pillX = getPillX(position.iconSide, index);
            const pillY = position.y - PILL_HEIGHT / 2; // Center the pill
            const circleX = getCircleX(position.iconSide, index, pillX);
            const circleY = position.y;

            // Extract value from vehicleData using the config key
            const rawValue = vehicleData?.[item.key] || item.value || '';
            const fullValue = String(rawValue);
            
            const maxValueLength = 18;
            const displayValue = fullValue.length > maxValueLength 
              ? fullValue.substring(0, maxValueLength) + '...' 
              : fullValue;

            // Get per-item animation state
            const itemState = itemStates[index] || { visible: false, opacity: 0, translateX: 350, scale: 1 };
            
            // Skip if not visible
            if (!itemState.visible && itemState.opacity === 0) {
              return null;
            }

            // Animation transforms - flip direction based on icon position
            // iconRight (1,3,5): slides from RIGHT (positive translateX)
            // iconLeft (2,4,6): slides from LEFT (negative translateX)
            const translateXDirection = position.iconSide === 'right' ? itemState.translateX : -itemState.translateX;
            const pillTransform = `translate(${translateXDirection}, 0) scale(${itemState.scale})`;
            const pillOpacity = itemState.opacity;

            return (
              <g
                key={item.key || index}
                opacity={pillOpacity}
                transform={pillTransform}
                style={{ transformOrigin: 'center' }}
              >
                {/* Grey Pill with shadow - width adjusted based on icon position */}
                <rect
                  x={pillX}
                  y={pillY}
                  width={getPillWidth(position.iconSide, index)}
                  height={PILL_HEIGHT}
                  rx={PILL_RADIUS}
                  ry={PILL_RADIUS}
                  fill="url(#pillGradient)"
                  fillOpacity="0.5"
                  stroke="rgba(255, 255, 255, 0.42)"
                  strokeWidth="1"
                  filter="url(#pillShadow)"
                />

                {/* Blue Circle (Outer) - Exact from outer.svg */}
                <circle
                  cx={circleX}
                  cy={circleY}
                  r={CIRCLE_RADIUS}
                  fill="url(#circleGradient)"
                  stroke="url(#circleBorderGradient)"
                  strokeWidth="1.03958"
                  strokeMiterlimit="10"
                  filter="url(#circleShadow)"
                />

                {/* Inner Circle - Exact from design specs */}
                <circle
                  cx={circleX}
                  cy={circleY}
                  r={VehicleHighlightsSVG.INNER_CIRCLE_RADIUS}
                  fill="url(#innerCircleGradient)"
                  stroke="url(#innerCircleBorderGradient)"
                  strokeWidth="1.04"
                />

                {/* Icon - Exact SVG from highlights.svg (shared with mweb view) */}
                {renderHighlightIcon({
                  label: item.label,
                  circleX,
                  circleY,
                  innerRadius: VehicleHighlightsSVG.INNER_CIRCLE_RADIUS,
                })}

                {/* Text and separator group */}
                <g>
                  {/* Calculate text position */}
                  {(() => {
    
                    const labelLines = [item.label];
                    const isMultiLineLabel = false;
                    
                    // Calculate text area boundaries (excluding circle with padding)
                    const textPadding = 15; // Padding from pill edges and circle
                    let textAreaStart, textAreaEnd, textAreaWidth, textCenterX;
                    
                    if (position.iconSide === 'right') {
                      // Icon on RIGHT (pills 2,4,6): text area is LEFT side
                      textAreaStart = pillX + textPadding;
                      textAreaEnd = circleX - CIRCLE_RADIUS - textPadding;
                      textAreaWidth = textAreaEnd - textAreaStart;
                      textCenterX = textAreaStart + (textAreaWidth / 2);
                    } else {
                      // Icon on LEFT (pills 1,3,5): text area is RIGHT side
                      textAreaStart = circleX + CIRCLE_RADIUS + textPadding;
                      const pillWidth = getPillWidth(position.iconSide, index);
                      textAreaEnd = pillX + pillWidth - textPadding;
                      textAreaWidth = textAreaEnd - textAreaStart;
                      textCenterX = textAreaStart + (textAreaWidth / 2);
                    }
                    
                    const labelY = pillY + PILL_HEIGHT / 2 - 14; // Positioned above center
                      // Value position - bottom section (20px, bold weight)
                    const valueY = pillY + PILL_HEIGHT / 2 + 16; // Positioned below center
                    
                    // Horizontal separator line - spans almost full text area width
                    const separatorY = pillY + PILL_HEIGHT / 2;
                    const separatorPadding = 1; // Padding from text area edges (reduced for longer line)
                    const separatorX1 = textAreaStart + separatorPadding;
                    const separatorX2 = textAreaEnd - separatorPadding;
                    
                    return (
                      <>
                        <text
                          x={textCenterX}
                          y={labelY}
                          fill="#000000"
                          fontSize="18"
                          fontFamily="Mona Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                          fontWeight="400"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {labelLines[0]}
                          {isMultiLineLabel && (
                            <tspan
                              x={textCenterX}
                              dy="20"
                            >
                              {labelLines[1]}
                            </tspan>
                          )}
                        </text>
                        
                        <line
                          x1={separatorX1}
                          y1={separatorY}
                          x2={separatorX2}
                          y2={separatorY}
                          stroke="#000000"
                          strokeWidth="1"
                        />
                        
                        {/* Value - bottom, centered (Mona Sans, 700/Bold, 20px) */}
                        <text
                          x={textCenterX}
                          y={valueY}
                          fill="#000000"
                          fontSize="20"
                          fontFamily="Mona Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                          fontWeight="700"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {displayValue || '(no value)'}
                          
                          {/* Tooltip for full value if truncated */}
                          {displayValue.includes('...') && (
                            <title>{fullValue}</title>
                          )}
                        </text>
                      </>
                    );
                  })()}
                </g>
              </g>
            );
          })}

          {/* Snake Line - revealed progressively from top to bottom using clip-path */}
          {isVisible && snakeProgress > 0 && (() => {
            const svgHeight = 971; // Total SVG height from viewBox
            const strokeWidth =35; // Snake line stroke width
            const buffer = strokeWidth * 10; // Extra buffer to account for stroke
            const totalHeight = svgHeight + buffer;
            const clipHeight = totalHeight * snakeProgress;
            
            return (
              <g>
                <defs>
                  <clipPath id="snakeClipPath">
                    <rect 
                      x="0" 
                      y="0" 
                      width="500" 
                      height={clipHeight}
                    />
                  </clipPath>
                </defs>
                
                <path
                  d={this.generateSnakeLine(specItemsConfig.length)}
                  fill="none"
                  stroke="url(#snakeLineGradient)"
                  strokeOpacity="0.55"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="13 13"
                  transform="scale(0.8036, 0.8000)"
                  clipPath="url(#snakeClipPath)"
                  style={{ transformOrigin: '0 0' }}
                />
              </g>
            );
          })()}

          {/* Debug Info */}
          {debug && (
            <text x="10" y="20" fill="#ff0000" fontSize="12">
              Frame: {currentFrame}/{totalFrames} | Phase: {phase} | Visible: {isVisible ? 'Yes' : 'No'}
            </text>
          )}
        </svg>
      </div>
    );
  }

  generateSnakeLine(pillCount) {
    if (pillCount === 0) return '';

    // Complete snake path with all segments (revealed progressively via clip-path)
    const fullPath = 'M168.44,3h-87.04C38.1,3,3,39,3,83.42s35.1,80.42,78.4,80.42h285.2c43.3,0,78.4,36,78.4,80.42s-35.1,80.42-78.4,80.42h-148.48M194.6,485.5h172c43.3,0,78.4,36,78.4,80.42s-35.1,80.42-78.4,80.42H81.4c-43.3,0-78.4,36-78.4,80.42s35.1,80.42,78.4,80.42h68.12M206.36,485.5h-124.96c-43.3,0-78.4-36-78.4-80.42s35.1-80.42,78.4-80.42h147.99M147.56,807.17h219.04c43.3,0,78.4,36,78.4,80.42s-35.1,80.42-78.4,80.42h-147.99';
    
    return fullPath;
  }
}

export default VehicleHighlightsSVG;
